// Pause-menu overlay shown when GameScene is paused. Buttons: Resume,
// Settings, Share, Restart, Quit. DOM-only — the scene owns the gameplay
// state and supplies the callbacks. Mounts a focus-trapped modal card on
// `open()`; `close()` removes it. Idempotent close.
//
// Two views swap inside the same card: main (Resume + actions) and settings
// (the same SettingsPanel the main-menu Settings screen uses, plus a Back
// button). The game stays paused while the settings view is open, so a
// theme/language change applies live on the next paint.

import {
  createCrestButton,
  createSettingsPanel,
  el,
  installFocusTrap,
  type FocusTrapHandle,
  type SettingsPanelHandle,
  type SettingsRow,
} from '@bn-games/ui-shell';
import { t } from '../i18n/I18n.js';

export interface PauseMenuCallbacks {
  readonly onResume: () => void;
  readonly onShare: () => void;
  readonly onRestart: () => void;
  readonly onQuit: () => void;
  readonly onSolve: () => void;
  /** Hide the Solve row when the puzzle is already finished — re-opening
   *  pause from the revealed-solution view shouldn't surface a no-op. */
  readonly canSolve: () => boolean;
  /** Supplies the settings rows the in-game settings view renders. The pause
   *  menu calls this on every render so live values stay accurate. */
  readonly getSettingsRows: () => readonly SettingsRow[];
}

type View = 'main' | 'settings';

let pauseTitleIdSeq = 0;

export class PauseMenu {
  private overlayNode: HTMLElement | null = null;
  private titleNode: HTMLElement | null = null;
  private bodyNode: HTMLElement | null = null;
  private actionsNode: HTMLElement | null = null;
  private trap: FocusTrapHandle | null = null;
  private settingsPanel: SettingsPanelHandle | null = null;
  private view: View = 'main';

  constructor(private readonly callbacks: PauseMenuCallbacks) {}

  isOpen(): boolean {
    return this.overlayNode !== null;
  }

  open(): void {
    if (this.isOpen()) return;
    const titleId = `sudoku-pause-title-${++pauseTitleIdSeq}`;

    const overlay = el('div', { class: 'modal-overlay' });
    const card = el('div', {
      class: 'modal-card',
      attrs: {
        role: 'dialog',
        'aria-modal': 'true',
        'aria-labelledby': titleId,
        tabindex: '-1',
      },
    });

    this.titleNode = el('h2', { class: 'modal-title', text: '', attrs: { id: titleId } });
    card.appendChild(this.titleNode);

    this.bodyNode = el('div', { class: 'modal-body' });
    card.appendChild(this.bodyNode);

    this.actionsNode = el('div', { class: 'modal-actions' });
    card.appendChild(this.actionsNode);

    overlay.appendChild(card);
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) this.handleResume();
    });

    document.body.appendChild(overlay);
    this.overlayNode = overlay;
    this.view = 'main';
    this.renderView();
    this.trap = installFocusTrap(card);
  }

  close(): void {
    if (this.trap !== null) {
      this.trap.release();
      this.trap = null;
    }
    if (this.overlayNode !== null) {
      this.overlayNode.remove();
      this.overlayNode = null;
      this.titleNode = null;
      this.bodyNode = null;
      this.actionsNode = null;
      this.settingsPanel = null;
    }
    this.view = 'main';
  }

  toggle(): void {
    if (this.isOpen()) this.handleResume();
    else this.open();
  }

  /** Re-render the settings panel; called when settings change externally. */
  refreshSettings(): void {
    this.settingsPanel?.rerender();
  }

  private renderView(): void {
    if (this.bodyNode === null || this.actionsNode === null || this.titleNode === null) return;
    while (this.bodyNode.firstChild) this.bodyNode.removeChild(this.bodyNode.firstChild);
    while (this.actionsNode.firstChild) this.actionsNode.removeChild(this.actionsNode.firstChild);
    this.settingsPanel = null;
    if (this.view === 'main') this.renderMain();
    else this.renderSettings();
  }

  private renderMain(): void {
    if (this.bodyNode === null || this.actionsNode === null || this.titleNode === null) return;
    this.titleNode.textContent = t('pause.title');
    // Settings stays inside the modal — swap views, don't close.
    this.bodyNode.appendChild(
      this.makeRow(
        createCrestButton({
          label: t('pause.settings'),
          small: true,
          onClick: () => {
            this.view = 'settings';
            this.renderView();
          },
        }),
      ),
    );
    this.bodyNode.appendChild(this.closingActionRow(t('pause.share'), this.callbacks.onShare));
    if (this.callbacks.canSolve()) {
      // Solve runs through a confirm dialog owned by the scene — don't pre-
      // close the pause menu so a cancelled confirm leaves the player exactly
      // where they started.
      this.bodyNode.appendChild(
        this.makeRow(
          createCrestButton({
            label: t('pause.solve'),
            small: true,
            onClick: () => this.callbacks.onSolve(),
          }),
        ),
      );
    }
    this.bodyNode.appendChild(this.closingActionRow(t('pause.restart'), this.callbacks.onRestart));
    this.bodyNode.appendChild(this.closingActionRow(t('pause.quit'), this.callbacks.onQuit));
    this.actionsNode.appendChild(
      createCrestButton({
        label: t('pause.resume'),
        primary: true,
        small: true,
        onClick: () => this.handleResume(),
      }),
    );
  }

  private renderSettings(): void {
    if (this.bodyNode === null || this.actionsNode === null || this.titleNode === null) return;
    this.titleNode.textContent = t('settings.title');
    const panel = createSettingsPanel({
      getRows: () => this.callbacks.getSettingsRows(),
      tabs: [
        { id: 'gameplay', label: t('settings.tabs.gameplay') },
        { id: 'interface', label: t('settings.tabs.interface') },
      ],
    });
    this.settingsPanel = panel;
    this.bodyNode.appendChild(panel.element);
    this.actionsNode.appendChild(
      createCrestButton({
        label: t('common.back'),
        small: true,
        onClick: () => {
          this.view = 'main';
          this.renderView();
        },
      }),
    );
    this.actionsNode.appendChild(
      createCrestButton({
        label: t('pause.resume'),
        primary: true,
        small: true,
        onClick: () => this.handleResume(),
      }),
    );
  }

  private handleResume(): void {
    this.close();
    this.callbacks.onResume();
  }

  private closingActionRow(label: string, onClick: () => void): HTMLElement {
    return this.makeRow(
      createCrestButton({
        label,
        small: true,
        onClick: () => {
          this.close();
          onClick();
        },
      }),
    );
  }

  private makeRow(control: HTMLElement): HTMLElement {
    return el('div', { class: 'game-overlay-section game-overlay-section--action' }, [
      control,
    ]);
  }
}
